---
id: helm-chart-review
title: Helm Chart Security Review
category: containers
difficulty: advanced
tags:
  - helm
  - kubernetes
  - security
  - chart
  - values
  - best-practices
description: >
  Reviews Helm chart templates and values for security misconfigurations,
  missing best practices, and production readiness.
whenToUse: >
  Before deploying a new Helm chart to production or when auditing
  existing deployments.
variables:
  - name: templates
    desc: Helm chart template files (deployment.yaml, service.yaml, etc.)
  - name: values
    desc: values.yaml content
  - name: environment
    desc: Target environment (dev, staging, production)
  - name: compliance
    desc: Compliance requirements (SOC2, PCI, etc.)
  - name: multi_tenant
    desc: Whether the cluster is multi-tenant (yes/no)
tips:
  - Review both templates AND default values — secure defaults matter more than secure overrides.
  - Check for hardcoded secrets or credentials in values.yaml.
  - Ensure pod disruption budgets exist for any production deployment.
---

You are a Kubernetes security engineer reviewing a Helm chart for production readiness.

## Chart Templates
```yaml
{{templates}}
```

## Values
```yaml
{{values}}
```

## Context
- Target environment: {{environment}}
- Compliance requirements: {{compliance}}
- Multi-tenant cluster: {{multi_tenant}}

## Review Areas
1. **Security Context** — Pod/container security context, runAsNonRoot, readOnlyRootFilesystem
2. **Resource Management** — Limits, requests, HPA configuration
3. **Network** — NetworkPolicy, service exposure, ingress TLS
4. **RBAC** — ServiceAccount, Role, least privilege
5. **Availability** — PDB, anti-affinity, topology spread
6. **Secrets** — Secret management, no hardcoded values
7. **Observability** — Health probes, metrics, logging

## Output Format (JSON)
{
  "production_ready": true,
  "score": "A | B | C | D | F",
  "findings": [
    {
      "severity": "critical | high | medium | low",
      "area": "security | resources | network | rbac | availability | secrets | observability",
      "template": "which template file",
      "issue": "description",
      "fix": "YAML snippet to fix it"
    }
  ],
  "missing_templates": ["templates that should be added (NetworkPolicy, PDB, etc.)"]
}

<!-- EXAMPLE_OUTPUT -->

{
  "production_ready": false,
  "score": "C",
  "findings": [
    {
      "severity": "critical",
      "area": "security",
      "template": "deployment.yaml",
      "issue": "Container runs as root — no securityContext defined",
      "fix": "securityContext:\n  runAsNonRoot: true\n  runAsUser: 1000\n  readOnlyRootFilesystem: true"
    }
  ],
  "missing_templates": ["networkpolicy.yaml", "poddisruptionbudget.yaml"]
}
